import {
  Badge,
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  styled,
  Switch,
  useTheme
} from "@mui/material";
import { get } from 'lodash';
import { useState } from 'react';
import Avatar from "react-avatar";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Account, ArrowDown, ArrowUp, BU, Earth, Signout } from "../../assets/svg-icons";
import {
  selected_business_unit
} from "../../config/localStorage";
import { adminType, languageLabels, supportedLanguage } from "../../constant/constant";
import {
  processNodeAndPermissions,
  redirectToPath,
} from "../../helper/com-helper";
import {
  createBuOptions,
  getPath,
  isAdminUser,
  setUserCookies,
  setUserLocalStorage
} from "../../helper/commonHelper";
import { loaderStateChange } from "../../store/Slices/loading";
import { getConfiguration, logout, updateSettings } from "../../utils/ApiHandler";
import { defaultTheme } from "../../utils/const";
import { ROUTES_PATH } from "../../utils/EndPoints";
import { SNOP } from "../../utils/RouteHelper/IBP/SNOP";
import { useThemeToggle } from "../SCAILayout";
import CustomTooltip from "../customTooltip";

const SCAIPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPopover-paper': {
    backgroundColor: theme.palette.primary.background,
    color: theme.palette.brandColor.grey3,
    borderRadius: "0.8rem",
    border: `0.15rem solid ${theme.palette.primary.borderColor}`
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700', // Example: green badge
    color: '#44b700',
    boxShadow: `0 0 0 0.2rem ${theme.palette.primary.background}`, // Badge outline
    width: '0.3rem', // Reduced width
    height: '0.7rem', // Reduced height
    borderRadius: '50%', // Keeps badge circular
    top: '0.1rem', // Adjusted top position for smaller badge
    right: '0.1rem', // Adjusted right position for smaller badge
  },
}));

const ThemeSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(9px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(12px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#1890ff',
        ...theme.applyStyles('dark', {
          backgroundColor: '#177ddc',
        }),
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: '#2C2C2F',
    boxSizing: 'border-box',
    ...theme.applyStyles('dark', {
      backgroundColor: 'rgba(255,255,255,.35)',
    }),
  },
}));

const MultiAvatar = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const themeMode = JSON.parse(localStorage.getItem("settings"))?.platform?.theme || defaultTheme;
  const { toggleMode } = useThemeToggle() || {};
  let user_metadata = JSON.parse(localStorage.getItem("user") || null);
  const selected_bu = JSON.parse(localStorage.getItem("selected_business_unit") || null);
  const selected_lng = localStorage.getItem("i18nextLng") || null;
  let business_units = get(user_metadata, "business_units") || [];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const buOption = createBuOptions(business_units);
  const isAdmin = isAdminUser();

  const [buExpand, setBuExpand] = useState(false);
  const [langExpand, setLangExpand] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleBuOption = async (option) => {

    if (Object.keys(sessionStorage) && Object.keys(sessionStorage).length) {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.includes("StaticHeader")) {
          sessionStorage.removeItem(key);
        }
      });
    }
    let value = option.value;
    if (selected_business_unit?.business_unit_id !== value) {
      dispatch(loaderStateChange({ isLoading: true }));
      setAnchorEl(null);
      const _selected_business_unit = {
        business_unit_id: option.value,
        business_unit_name: option.label,
      };
      localStorage.setItem(
        "selected_business_unit",
        JSON.stringify(_selected_business_unit)
      );
      // let user = JSON.parse(Cookies.get("user") || null);
      let user = { ...user_metadata };
      if (!user) return;
      const newUser = setUserLocalStorage(user, value);
      const userPermissions = newUser?.permissions;
      const roles = newUser?.roles;
      if (!userPermissions || !roles) return;
      const path = getPath(userPermissions, roles);
      if (user.tenant_id && _selected_business_unit?.business_unit_id) {
        const configurationResult = await getConfiguration(
          user.access,
          user.tenant_id,
          _selected_business_unit?.business_unit_id
        );
        localStorage.removeItem("ibp_calender");
        localStorage.removeItem("snop");
        const { permissions } = processNodeAndPermissions(SNOP);
        let isSandOpPermission = userPermissions.includes(permissions[0]);
        if (!isSandOpPermission) {
          redirectToPath(path);
          return;
        }
        if (configurationResult?.status === 200) {
          setUserCookies(newUser);
          redirectToPath(path);
        }
      } else {
        setUserCookies(newUser);
        redirectToPath(path);
      }
      dispatch(loaderStateChange({ isLoading: true }));
    }
  };

  const handleLanguageChange = (lng) => {
    if (supportedLanguage.includes(lng) && lng !== selected_lng) {
      dispatch(loaderStateChange({ isLoading: true }));
      setTimeout(() => {
        localStorage.setItem("i18nextLng", lng);
        callUpdateSettings(lng);
        dispatch(loaderStateChange({ isLoading: false }));
        window.location.reload();
      }, 2000);
    }
  };

  const callUpdateSettings = async (selectedLng) => {
    const settings = JSON.parse(localStorage.getItem("settings"));
    const tenant_id = JSON.parse(localStorage.getItem("user"))?.tenant_id;
    const bu_id = JSON.parse(localStorage.getItem("selected_business_unit"))?.business_unit_id;
    const platform = JSON.parse(localStorage.getItem("settings"))?.platform || {};

    const payload = {
      ...settings,
      "platform": { ...platform, language: selectedLng }
    }
    localStorage.setItem("settings", JSON.stringify(payload));
    await updateSettings({ payload: payload, tenant_id, bu_id });
  }

  const handleClickMyProfile = () => {
    navigate(ROUTES_PATH.MY_PROFILE);
  };

  const handleLogout = () => {
    logout(navigate, dispatch);
    document.body.style.backgroundColor = "none"
  };

  const profileDP = localStorage.getItem("profileDP") || "unknown user"; // by default - User
  return (
    <>
      <Box
        onClick={(e) => {
          handleClick(e);
        }}
        sx={{
          cursor: "pointer",
        }}
      >
        <StyledBadge
          overlap="rectangular" // Use rectangular to align with a square avatar
          anchorOrigin={{ vertical: "top", horizontal: "right" }} // Badge at top-right
          variant="dot"
        >
          <Avatar
            name={profileDP}
            value={profileDP}
            title={profileDP}
            size="28" // Size remains the same
            textSizeRatio={1}
            textMarginRatio={0.25}
            className="avatar"

          />
        </StyledBadge>
      </Box>
      <SCAIPopover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          padding: "1rem",
          marginTop: "0.2rem",
        }}
        className="profile-dropdown-container"
      >
        <Box
          sx={{
            minWidth: "12.5rem",
            maxHeight: "auto",
          }}
        >
          <List>
            <ListItem disablePadding sx={{ display: "block" }}>
              {user_metadata?.roles?.some(role => !adminType[role]) &&
                <>
                  <ListItemButton
                    disableRipple
                    onClick={handleClickMyProfile}
                    sx={{
                      minHeight: "2.5rem",
                      justifyContent: "center",
                      px: "1.4rem",
                      py: "0rem",
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      '&:active': {
                        backgroundColor: 'transparent',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'transparent',
                      },
                      '&:focus': {
                        backgroundColor: 'transparent',
                      },
                      '&.Mui-focusVisible': {
                        backgroundColor: 'transparent',
                      },
                    }}

                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        pr: "1rem",
                        justifyContent: "center",
                        color: theme.palette.primary.textColor,
                      }}
                    >
                      <Account color={theme.palette.primary.textColor} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t("My Profile")}
                      primaryTypographyProps={{
                        fontSize: "1rem", // Set your desired font size
                        color: theme.palette.primary.textColor, // Optional: Add color here
                      }}
                    />
                  </ListItemButton>
                  <Divider
                    variant="middle"
                    sx={{
                      borderWidth: "0.09rem",
                      mt: "0.2rem",
                    }}
                  />

                  <ListItemButton
                    disableRipple
                    onClick={() => setBuExpand((prev) => !prev)}
                    sx={{
                      minHeight: "2.5rem",
                      justifyContent: "center",
                      px: "1.4rem",
                      py: "0rem",
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      '&:active': {
                        backgroundColor: 'transparent',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'transparent',
                      },
                      '&:focus': {
                        backgroundColor: 'transparent',
                      },
                      '&:focus-visible': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        pr: "1rem",
                        justifyContent: "center",
                        color: theme.palette.primary.textColor, // Default icon color
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <BU color={theme.palette.primary.textColor} />
                    </ListItemIcon>
                    <ListItemText
                      primary={selected_bu?.business_unit_name}
                      primaryTypographyProps={{
                        fontSize: "1rem", // Set your desired font size
                        color: theme.palette.primary.textColor, // Optional: Add color here
                      }}
                    />
                    {buOption?.length > 1 && (
                      <CustomTooltip
                        title={t("Change BU")}
                        arrow
                        placement="left"
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            pl: "1rem",
                            justifyContent: "center",
                            color: theme.palette.primary.textColor, // Default icon color
                          }}
                        >
                          {buExpand ? (
                            <ArrowUp color={theme.palette.primary.textColor} />
                          ) : (
                            <ArrowDown color={theme.palette.primary.textColor} />
                          )}
                        </ListItemIcon>
                      </CustomTooltip>
                    )}
                  </ListItemButton>
                  {buOption?.length > 1 && (
                    <Collapse in={buExpand} timeout="auto" unmountOnExit>
                      {buOption?.map((bu) => (
                        <ListItemButton
                          key={bu?.value}
                          onClick={() => handleBuOption(bu)}
                          sx={{
                            minHeight: "2.5rem",
                            justifyContent: "center",
                            pl: "3.5rem",
                            py: "0rem",
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                            '&:active': {
                              backgroundColor: 'transparent',
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'transparent',
                            },
                            '&:focus': {
                              backgroundColor: 'transparent',
                            },
                            '&:focus-visible': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          <ListItemText
                            primary={bu?.label}
                            primaryTypographyProps={{
                              fontSize: "1rem", // Set your desired font size
                              color: theme.palette.primary.textColor, // Optional: Add color here
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </Collapse>
                  )}
                  <Divider
                    variant="middle"
                    sx={{
                      borderWidth: "0.09rem",
                      mt: "0.2rem",
                    }}
                  />
                </>
              }

              <ListItemButton
                disableRipple
                onClick={() => setLangExpand((prev) => !prev)}
                sx={{
                  minHeight: "2.5rem",
                  justifyContent: "center",
                  px: "1.4rem",
                  py: "0rem",
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&:active': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                  },
                  '&:focus': {
                    backgroundColor: 'transparent',
                  },
                  '&:focus-visible': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    pr: "1rem",
                    justifyContent: "center",
                    color: theme.palette.primary.textColor,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {/* <img src={earthTranslation} alt="earth" width={15} height={15} /> */}
                  <Earth color={theme.palette.primary.textColor} />
                </ListItemIcon>
                <ListItemText
                  data-testid="language-id"
                  primary={languageLabels[selected_lng] || t("Select Language")}
                  primaryTypographyProps={{
                    fontSize: "1rem",
                    color: theme.palette.primary.textColor,
                  }}
                />
                {supportedLanguage?.length > 1 && (
                  <CustomTooltip
                    title={t("Change Language")}
                    arrow
                    placement="left"
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        pl: "1rem",
                        justifyContent: "center",
                        color: theme.palette.primary.textColor, // Default icon color
                      }}
                    >
                      {langExpand ? (
                        <ArrowUp color={theme.palette.primary.textColor} />
                      ) : (
                        <ArrowDown color={theme.palette.primary.textColor} />
                      )}
                    </ListItemIcon>
                  </CustomTooltip>
                )}
              </ListItemButton>
              {supportedLanguage?.length > 1 && (
                <Collapse in={langExpand} timeout="auto" unmountOnExit>
                  {supportedLanguage?.map((lng) => (
                    <ListItemButton
                      key={languageLabels?.[lng]}
                      onClick={() => handleLanguageChange(lng)}
                      sx={{
                        minHeight: "2.5rem",
                        justifyContent: "center",
                        pl: "3.5rem",
                        py: "0rem",
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                        '&:active': {
                          backgroundColor: 'transparent',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'transparent',
                        },
                        '&:focus': {
                          backgroundColor: 'transparent',
                        },
                        '&:focus-visible': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <ListItemText
                        primary={languageLabels?.[lng]}
                        primaryTypographyProps={{
                          fontSize: "1rem",
                          color: theme.palette.primary.textColor,
                        }}
                      />
                    </ListItemButton>
                  ))}
                </Collapse>
              )}
              <Divider
                variant="middle"
                sx={{
                  borderWidth: "0.09rem",
                  mt: "0.2rem",
                }}
              />

              {/* Theme Toggler */}
              <ListItemButton
                disableRipple
                sx={{
                  minHeight: "2.5rem",
                  justifyContent: "center",
                  px: "1.4rem",
                  py: "0rem",
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&:active': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                  },
                  '&:focus': {
                    backgroundColor: 'transparent',
                  },
                  '&:focus-visible': {
                    backgroundColor: 'transparent',
                  },
                }}
                onClick={toggleMode}
              >
                <ThemeSwitch
                  sx={{
                    mr: "0.5rem",
                  }}
                  checked={themeMode !== defaultTheme}
                />
                <ListItemText
                  primary={t(themeMode[0]?.toUpperCase() + themeMode?.slice(1))}
                  primaryTypographyProps={{
                    fontSize: "1rem", // Set your desired font size
                    color: theme.palette.primary.textColor, // Optional: Add color here
                  }}
                />
              </ListItemButton>
              <Divider
                variant="middle"
                sx={{
                  borderWidth: "0.09rem",
                  mt: "0.2rem",
                }}
              />

              {/* Logout */}
              <ListItemButton
                disableRipple
                onClick={handleLogout}
                sx={{
                  minHeight: "2.5rem",
                  justifyContent: "center",
                  px: "1.4rem",
                  py: "0rem",
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&:active': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                  },
                  '&:focus': {
                    backgroundColor: 'transparent',
                  },
                  '&:focus-visible': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    pr: "1rem",
                    justifyContent: "center",
                    color: theme.palette.primary.textColor, // Default icon color
                  }}
                >
                  <Signout color={theme.palette.primary.textColor} />
                </ListItemIcon>
                <ListItemText
                  primary={t("Logout")}
                  primaryTypographyProps={{
                    fontSize: "1rem", // Set your desired font size
                    color: theme.palette.primary.textColor, // Optional: Add color here
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </SCAIPopover>
    </>
  );
}

export default MultiAvatar;
